/* eslint-disable no-template-curly-in-string */
/* eslint-disable max-len */
import React from "react";
import { Formik, FieldArray } from "formik";
import Grid from "@mui/material/Grid";
import uniqueId from "lodash/uniqueId";
import * as Yup from "yup";
import { format, isValid } from "date-fns";
import {
  AutoSaveField as Field,
  CustomSelect,
  InlineRadioGroup,
  DatePicker,
  Input,
  TimePicker,
  InputGroup,
  LinkedField
} from "../../../../../components/Form";
import { ContinueButton } from "../../../../../components/Buttons/ContinueButton";
import { Divider } from "../../../../../components/Divider";
import {
  UAE_MOBILE_PHONE_REGEX,
  NUMBER_REGEX,
  MIN_NON_UAE_PHONE_LENGTH,
  MAX_NON_UAE_PHONE_LENGTH
} from "../../../../../utils/validation";
import { getInvalidMessage, getMinDateMessage } from "../../../../../utils/getValidationMessage";
import { ArrayRemoveButton } from "../Buttons/ArrayRemoveButton";
import { ArrayAddButton } from "../Buttons/ArrayAddButton";
import { UAE_CODE, DATE_FORMAT, DATE_TIME_FORMAT } from "../../../../../constants";
import { ContinueBtn, FormStyle } from "../styled";
import { ScrollToError } from "components/ScrollToError";

const checkPhoneValidation = (countryCode, mobileNumber) => {
  if (mobileNumber) {
    if (countryCode === UAE_CODE) {
      return UAE_MOBILE_PHONE_REGEX.test(mobileNumber);
    } else {
      const maxLength = MAX_NON_UAE_PHONE_LENGTH - (countryCode ? countryCode.length : 3);
      return (
        NUMBER_REGEX.test(mobileNumber) &&
        mobileNumber.length >= MIN_NON_UAE_PHONE_LENGTH &&
        mobileNumber.length <= maxLength
      );
    }
  }
  return true;
};
const validationSchema = Yup.object().shape({
  landlineValidations: Yup.array().of(
    Yup.object().shape({
      successfullyValidated: Yup.string().nullable(),
      dateOfVisit: Yup.date()
        .nullable()
        .min(new Date(1900, 0, 1), getMinDateMessage("Date of Validation"))
        .max(new Date(), getInvalidMessage("Date of Validation"))
        .typeError(getInvalidMessage("Date of Validation")),
      timeOfVisit: Yup.string().nullable(),
      roName: Yup.string().nullable()
    })
  ),
  mobileValidation: Yup.array().of(
    Yup.object().shape({
      successfullyValidated: Yup.string().nullable(),
      dateOfVisit: Yup.date()
        .nullable()
        .min(new Date(1900, 0, 1), getMinDateMessage("Date of Validation"))
        .max(new Date(), getInvalidMessage("Date of Validation"))
        .typeError(getInvalidMessage("Date of Validation")),
      timeOfVisit: Yup.string().nullable(),
      roName: Yup.string().nullable(),
      secondaryEmail: Yup.string().nullable(),
      secondaryCountryCode: Yup.string().nullable(),
      secondaryMobileNo: Yup.string()
        .nullable()
        .test("phone validation", "Secondary Contact is Invalid", function(value) {
          const { secondaryCountryCode } = this.parent;
          return checkPhoneValidation(secondaryCountryCode, value);
        })
    })
  ),
  directoryValidations: Yup.array().of(
    Yup.object().shape({
      successfullyValidated: Yup.string().nullable(),
      dateOfVisit: Yup.date()
        .nullable()
        .min(new Date(1900, 0, 1), getMinDateMessage("Date of Validation"))
        .max(new Date(), getInvalidMessage("Date of Validation"))
        .typeError(getInvalidMessage("Date of Validation")),
      timeOfVisit: Yup.string().nullable(),
      roName: Yup.string().nullable(),
      secondaryEmail: Yup.string()
        .nullable()
        .max(50, "Max ${max} characters allowed")
    })
  )
});

const changeDateProspectHandler = (_, value, path) =>
  isValid(value) && { [path]: format(value, DATE_FORMAT) };

const changeTimeProspectHandler = (_, value, path) =>
  isValid(value) && { [path]: format(new Date(value), DATE_TIME_FORMAT) };

export const CompanyContactDetails = ({
  handleContinue,
  createFormChangeHandler,
  organizationInfoRO,
  updateRoProspect
}) => {
  const roProspectPath = "roProspect.organizationInfoRO";
  return (
    <Formik
      initialValues={{
        landlineValidations: organizationInfoRO.landlineValidation.map(item => ({
          ...item,
          id: uniqueId()
        })),
        mobileValidation: organizationInfoRO.mobileValidation.map(item => ({
          ...item,
          id: uniqueId()
        })),
        directoryValidations: organizationInfoRO.directoryValidation.map(item => ({
          ...item,
          id: uniqueId()
        }))
      }}
      validateOnChange={false}
      validationSchema={validationSchema}
      onSubmit={handleContinue}
    >
      {createFormChangeHandler(({ values, setFieldValue }) => {
        return (
          <FormStyle>
            <ScrollToError />
            <Grid container spacing={3}>
              <Grid item sm={12} xs={12}>
                <h5 className="mt-0 mb-0"> Directory Check </h5>
              </Grid>
            </Grid>
            <FieldArray
              name="directoryValidations"
              render={arrayHelper =>
                values.directoryValidations.map((item, index) => {
                  const directoryDltBtn = values.directoryValidations.length > 1;
                  return (
                    <Grid container spacing={3} key={item.id}>
                      <Grid container item alignItems="center" sm={4} xs={12}>
                        <p>Successfully validated?</p>
                      </Grid>
                      <Grid item sm={directoryDltBtn ? 6 : 8} xs={directoryDltBtn ? 10 : 12}>
                        <Field
                          name={`directoryValidations[${index}].successfullyValidated`}
                          path={`${roProspectPath}.directoryValidation[${index}].successfullyValidated`}
                          label=""
                          component={InlineRadioGroup}
                          datalistId="YesNoList"
                          typeRadio
                          contextualHelpProps={{ isDisableHoverListener: false }}
                        />
                      </Grid>
                      {directoryDltBtn && (
                        <Grid item sm={2} xs={2}>
                          <ArrayRemoveButton
                            arrayHelpers={arrayHelper}
                            dataArray={organizationInfoRO.directoryValidation}
                            itemIndex={index}
                            updateRoProspect={updateRoProspect}
                            prospectPath={`${roProspectPath}.directoryValidation`}
                            title="Delete"
                          />
                        </Grid>
                      )}
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`directoryValidations[${index}].dateOfVisit`}
                          path={`${roProspectPath}.directoryValidation[${index}].dateOfVisit`}
                          label="CPV Date"
                          component={DatePicker}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                          changeProspect={changeDateProspectHandler}
                          maxDate={new Date()}
                        />
                      </Grid>
                      <Grid item sm={4} xs={12}>
                        <Field
                          name={`directoryValidations[${index}].timeOfVisit`}
                          path={`${roProspectPath}.directoryValidation[${index}].timeOfVisit`}
                          label="Time of Validation"
                          component={TimePicker}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                          changeProspect={changeTimeProspectHandler}
                        />
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`directoryValidations[${index}].roName`}
                          path={`${roProspectPath}.directoryValidation[${index}].roName`}
                          label="Name of RO/RM"
                          component={CustomSelect}
                          datalistId="rormName"
                          tabIndex="0"
                        />
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`directoryValidations[${index}].secondaryEmail`}
                          path={`${roProspectPath}.directoryValidation[${index}].secondaryEmail`}
                          label="Source"
                          component={Input}
                          tabIndex="0"
                        />
                      </Grid>
                      <Grid container item alignItems="center" sm={12} xs={12}>
                        <ArrayAddButton
                          title="Redo"
                          limit={5}
                          addedItem={{
                            successfullyValidated: "",
                            dateOfVisit: "",
                            timeOfVisit: "",
                            roName: ""
                          }}
                          dataArray={values.directoryValidations}
                          arrayHelpers={arrayHelper}
                        />
                      </Grid>
                    </Grid>
                  );
                })
              }
            />

            <Divider.Dark />
            <Grid container spacing={3}>
              <Grid item sm={12} xs={12}>
                <h5 className="mb-0"> Landline Validation </h5>
              </Grid>
            </Grid>
            <FieldArray
              name="landlineValidations"
              render={arrayHelper =>
                values.landlineValidations.map((item, index) => {
                  const landlineDltBtn = values.landlineValidations.length > 1;
                  return (
                    <Grid container spacing={3} key={item.id}>
                      <Grid container alignItems="center" item sm={4} xs={12}>
                        <p>Successfully validated?</p>
                      </Grid>
                      <Grid item sm={landlineDltBtn ? 6 : 8} xs={landlineDltBtn ? 10 : 12}>
                        <Field
                          name={`landlineValidations[${index}].successfullyValidated`}
                          path={`${roProspectPath}.landlineValidation[${index}].successfullyValidated`}
                          label=""
                          component={InlineRadioGroup}
                          datalistId="YesNoList"
                          typeRadio
                          contextualHelpProps={{ isDisableHoverListener: false }}
                        />
                      </Grid>
                      {landlineDltBtn && (
                        <Grid item sm={2} xs={2}>
                          <ArrayRemoveButton
                            arrayHelpers={arrayHelper}
                            dataArray={organizationInfoRO.landlineValidation}
                            itemIndex={index}
                            updateRoProspect={updateRoProspect}
                            prospectPath={`${roProspectPath}.landlineValidation`}
                            title="Delete"
                          />
                        </Grid>
                      )}
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`landlineValidations[${index}].dateOfVisit`}
                          path={`${roProspectPath}.landlineValidation[${index}].dateOfVisit`}
                          label="Date of Validation"
                          component={DatePicker}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                          changeProspect={changeDateProspectHandler}
                          maxDate={new Date()}
                        />
                      </Grid>
                      <Grid item sm={4} xs={12}>
                        <Field
                          name={`landlineValidations[${index}].timeOfVisit`}
                          path={`${roProspectPath}.landlineValidation[${index}].timeOfVisit`}
                          label="Time of Validation"
                          component={TimePicker}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                          changeProspect={changeTimeProspectHandler}
                        />
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`landlineValidations[${index}].roName`}
                          path={`${roProspectPath}.landlineValidation[${index}].roName`}
                          label="Name of RO/RM"
                          component={CustomSelect}
                          datalistId="rormName"
                          tabIndex="0"
                        />
                      </Grid>
                      <Grid container item alignItems="center" sm={12} xs={12}>
                        <ArrayAddButton
                          title="Redo"
                          limit={5}
                          addedItem={{
                            successfullyValidated: "",
                            dateOfVisit: "",
                            timeOfVisit: "",
                            roName: ""
                          }}
                          dataArray={values.landlineValidations}
                          arrayHelpers={arrayHelper}
                        />
                      </Grid>
                    </Grid>
                  );
                })
              }
            />

            <Divider.Dark />
            <Grid container spacing={3}>
              <Grid item sm={12} xs={12}>
                <h5 className="mb-0"> Mobile Validation </h5>
              </Grid>
            </Grid>
            <FieldArray
              name="mobileValidation"
              render={arrayHelper =>
                values.mobileValidation.map((item, index) => {
                  const mobileDltBtn = values.mobileValidation.length > 1;
                  return (
                    <Grid container spacing={3} key={item.id}>
                      <Grid container alignItems="center" item sm={4} xs={12}>
                        <p>Successfully validated?</p>
                      </Grid>
                      <Grid item sm={mobileDltBtn ? 6 : 8} xs={mobileDltBtn ? 10 : 12}>
                        <Field
                          name={`mobileValidation[${index}].successfullyValidated`}
                          path={`${roProspectPath}.mobileValidation[${index}].successfullyValidated`}
                          label=""
                          component={InlineRadioGroup}
                          datalistId="YesNoList"
                          typeRadio
                          contextualHelpProps={{ isDisableHoverListener: false }}
                        />
                      </Grid>
                      {mobileDltBtn && (
                        <Grid item sm={2} xs={2}>
                          <ArrayRemoveButton
                            arrayHelpers={arrayHelper}
                            dataArray={organizationInfoRO.mobileValidation}
                            itemIndex={index}
                            updateRoProspect={updateRoProspect}
                            prospectPath={`${roProspectPath}.mobileValidation`}
                            title="Delete"
                          />
                        </Grid>
                      )}
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`mobileValidation[${index}].dateOfVisit`}
                          path={`${roProspectPath}.mobileValidation[${index}].dateOfVisit`}
                          label="Date of Validation"
                          component={DatePicker}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                          changeProspect={changeDateProspectHandler}
                          maxDate={new Date()}
                        />
                      </Grid>
                      <Grid item sm={4} xs={12}>
                        <Field
                          name={`mobileValidation[${index}].timeOfVisit`}
                          path={`${roProspectPath}.mobileValidation[${index}].timeOfVisit`}
                          label="Time of Validation"
                          component={TimePicker}
                          InputProps={{
                            inputProps: { tabIndex: 0 }
                          }}
                          changeProspect={changeTimeProspectHandler}
                        />
                      </Grid>
                      <Grid item sm={6} xs={12}>
                        <Field
                          name={`mobileValidation[${index}].roName`}
                          path={`${roProspectPath}.mobileValidation[${index}].roName`}
                          label="Name of RO/RM"
                          component={CustomSelect}
                          datalistId="rormName"
                          tabIndex="0"
                        />
                      </Grid>

                      <Grid item sm={6} xs={12}>
                        <InputGroup>
                          <Field
                            name={`mobileValidation[${index}].secondaryCountryCode`}
                            linkedFieldName={`mobileValidation[${index}].secondaryMobileNo`}
                            path={`${roProspectPath}.mobileValidation[${index}].secondaryCountryCode`}
                            linkedPath={`${roProspectPath}.mobileValidation[${index}].secondaryMobileNo`}
                            datalistId="countryCode"
                            component={CustomSelect}
                            isSearchable
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                          />

                          <LinkedField
                            name={`mobileValidation[${index}].secondaryMobileNo`}
                            path={`${roProspectPath}.mobileValidation[${index}].secondaryMobileNo`}
                            linkedFieldName={`mobileValidation[${index}].secondaryCountryCode`}
                            linkedPath={`${roProspectPath}.mobileValidation[${index}].secondaryCountryCode`}
                            label="Secondary contact"
                            placeholder="Secondary contact"
                            component={Input}
                            InputProps={{
                              inputProps: { tabIndex: 0 }
                            }}
                          />
                        </InputGroup>
                      </Grid>
                      <Grid container item alignItems="center" sm={12} xs={12}>
                        <ArrayAddButton
                          title="Redo"
                          limit={5}
                          addedItem={{
                            successfullyValidated: "",
                            dateOfVisit: "",
                            timeOfVisit: "",
                            roName: "",
                            secondaryEmail: "",
                            secondaryCountryCode: UAE_CODE,
                            secondaryMobileNo: ""
                          }}
                          dataArray={values.mobileValidation}
                          arrayHelpers={arrayHelper}
                        />
                      </Grid>
                    </Grid>
                  );
                })
              }
            />

            <Grid container direction="row" justifyContent="flex-end" mb={2}>
              <ContinueBtn component="span">
                <ContinueButton type="submit" data-testid="continue-button" />
              </ContinueBtn>
            </Grid>
          </FormStyle>
        );
      })}
    </Formik>
  );
};
